/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import { observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IEveMapResources } from '../eve-map.props.autogenerated';
import { IDealerLocatorLocationLineItemResources, DealerLocatorLocationLineItemComponent } from './eve-map-location-line-item';
import { IDealerLocations, IDealerLocation } from '../eve-map';
import { ICoreContext } from '@msdyn365-commerce/core-internal';

/**
 * Store selector location lines props interface.
 */
export interface IStoreSelectorLocationLinesProps {
    locations?: IDealerLocations[];
    resources: IEveMapResources;
    preferredDistanceUnit?: string;
    selectedStoreLocationId?: string;
    displayList?: boolean;
    onClick: (locationId: string | undefined) => void;
    context: ICoreContext;
}

/**
 * Displays all the location line items, or null if none exists.
 */
@observer
export class DealerLocatorLocationLines extends React.PureComponent<IStoreSelectorLocationLinesProps> {
    @observable private _stores: IDealerLocations[] | undefined;

    private readonly selectedLocationRef: React.RefObject<HTMLDivElement>;

    private storeCounter: number;

    public constructor(props: IStoreSelectorLocationLinesProps) {
        super(props);
        this.selectedLocationRef = React.createRef();
        this.storeCounter = 0;
    }

    public componentDidUpdate(previousProps: IStoreSelectorLocationLinesProps): void {
        if (this.props.selectedStoreLocationId !== previousProps.selectedStoreLocationId) {
            this._scrollIntoView();
        }
    }

    public componentDidMount(): void {
        if (this.props.locations) {
            this._stores = [...this.props.locations];
        }

        reaction(
            () => this.props.locations,
            () => {
                if (this.props.locations) {
                    this._stores = [...this.props.locations];
                }
            }
        );
    }

    public render(): JSX.Element | null {
        if (!ArrayExtensions.hasElements(this._stores)) {
            return null;
        }
        this.storeCounter = 0;

        return (
            <div className={classnames('ms-store-select__locations', { show: this.props.displayList })} role='list'>
                {this._stores.map((store, index) => {
                    return this._renderStoreLocation(store, index);
                })}
            </div>
        );
    }

    /**
     * On click Handler function.
     * @param orgUnitLocation -OrgUnit location.
     * @returns Click action on orgUnit Location.
     */
    private readonly onClickHandler = (orgUnitLocation: IDealerLocation | undefined) => () => {
        this.props.onClick(orgUnitLocation?.OrgUnitNumber);
    };

    private _renderStoreLocation(store: IDealerLocations, index: number): JSX.Element | undefined {
        const orgUnitLocation = store?.OrgUnitLocation;
        const selectedStoreLocationId = this.props.selectedStoreLocationId;
        const defaultAriaSetsize = 0;
        if (orgUnitLocation) {
            ++this.storeCounter;
            return (
                <div
                    className={classnames('ms-store-select__location', {
                        selected: selectedStoreLocationId === orgUnitLocation.OrgUnitNumber
                    })}
                    key={index}
                    role='listitem'
                    aria-setsize={ArrayExtensions.hasElements(this._stores) ? this._stores.length : defaultAriaSetsize}
                    aria-posinset={this.storeCounter}
                    onClick={this.onClickHandler(orgUnitLocation)}
                    ref={selectedStoreLocationId === orgUnitLocation.OrgUnitNumber ? this.selectedLocationRef : undefined}
                >
                    <DealerLocatorLocationLineItemComponent
                        location={orgUnitLocation}
                        resources={this._mapResources(this.props.resources)}
                        preferredDistanceUnit={this.props.preferredDistanceUnit!}
                        index={this.storeCounter.toString()}
                        phoneNumber={orgUnitLocation.PhoneNumber || ''}
                        context={this.props.context}
                    />
                </div>
            );
        }
        return undefined;
    }

    /**
     * Scroll into view.
     */
    private readonly _scrollIntoView = () => {
        // scroll into view to have the page scroll to the top of the list when the page changes.
        const selectedLocationDiv = this.selectedLocationRef.current;
        const parent = selectedLocationDiv?.parentElement;
        if (selectedLocationDiv && parent) {
            // if (parent.scrollTop + parent.clientHeight > selectedLocationDiv.offsetTop) {
            //     parent.scrollTop = selectedLocationDiv.offsetTop - parent.offsetTop - 20;
            // } else if (selectedLocationDiv.offsetTop + selectedLocationDiv.clientHeight > parent.scrollTop + parent.clientHeight) {
            //     // parent.scrollTop = selectedLocationDiv.offsetTop + selectedLocationDiv.clientHeight - parent.clientHeight;
            // }

            // Set the parent to the location of the selected dealer and slide it down by a small 20 px buffer.
            parent.scrollTop = selectedLocationDiv.offsetTop - parent.offsetTop - 20;
        }
    };

    /**
     * Is current location.
     * @param location - Org unit location.
     * @returns The current location.
     */
    // private readonly _isCurrentLocation = (location: OrgUnitLocation | undefined): boolean =>
    //     (this.props.alreadySelectedLocation && location && this.props.alreadySelectedLocation.OrgUnitNumber === location.OrgUnitNumber) ||
    //     false;

    /**
     * Map resources.
     * @param resources - Store resoureces.
     * @returns Resources.
     */
    private readonly _mapResources = (resources: IEveMapResources): IDealerLocatorLocationLineItemResources => {
        return {
            contactInfoHeader: resources.contactText,
            dealerDetailsHeader: resources.dealerDetailsText,
            dealerDetailsManufacturerCodeText: resources.dealerDetailsManufacturerCodeText,
            dealerDetailsLaborRateText: resources.dealerDetailsLaborRateText,
            dealerDetailsDescriptionText: resources.dealerDetailsDescriptionText,
            dealerDetailsNotesText: resources.dealerDetailsNotesText,
            dealerDetailsContractCodeText: resources.dealerDetailsContractCodeText
        };
    };
}
